import type * as Flows from './types.no-deps'
import type * as Guide from '../guides/types.no-deps'
import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import { hasInputs, hasRequired } from './typeGuards.no-deps'
import { currentAnswerIsEmpty } from './answer.no-deps'
import { answerValueToDate } from './dateTime.no-deps'
import { oneDayInMs } from '../../lib/duration.no-deps'
import { type LoopPathIds, type LoopPath, toLoopPath } from './LoopPath.no-deps'

type Element = Flows.Section | Flows.Element | Flows.NestedElement

export function validateAnswer(
    answer: Flows.Answer,
    sectionId: Flows.ElementId,
    element: Element,
    reportStartDate: Date,
    isSubtaskOpen: (reportId: Flows.ReportId) => boolean,
    loopPathIds: LoopPathIds,
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide }>
): Flows.ValidationFailure[] {
    const reasons = getReasons(answer, element, reportStartDate, isSubtaskOpen, guideElementsContextMap)
    const loopPath = toLoopPath(loopPathIds)
    return reasons.map((reason) => reasonToFailure(sectionId, element, loopPath, reason))
}

export function reasonToFailure(
    sectionId: Flows.ElementId,
    element: Element,
    loopPath: LoopPath,
    reason: Flows.FailureReason
): Flows.ValidationFailure {
    return { sectionId, elementId: element.id, loopPath, reason }
}

function elementRequiresAnswer(
    element: Element,
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide }>
): boolean {
    const isRequired = hasRequired(element) && element.required

    if (element.type === 'guide' && !element.dynamicGuideSettings) {
        // Only guides which we know for sure are not deleted require a response.
        const syncGuide = guideElementsContextMap.get(element.guideId)?.guide
        const guideIsNotDeleted = syncGuide && !syncGuide.isDeleted
        return isRequired && guideIsNotDeleted
    }

    if (element.type === 'guide' && element.dynamicGuideSettings) {
        const dynamicGuides = Array.from(guideElementsContextMap.values())
            .map((value) => value.guide)
            .filter((guide) => guide.units?.includes(element.dynamicGuideSettings.tagId))
        const dynamicGuidesAreNotDeleted: boolean = dynamicGuides.every((guide) => !guide.isDeleted)
        return isRequired && dynamicGuidesAreNotDeleted
    }

    return isRequired
}

function getReasons(
    answer: Flows.Answer,
    element: Element,
    reportStartDate: Date,
    isSubtaskOpen: (reportId: Flows.ReportId) => boolean,
    guideElementsContextMap: Map<Guide.Id, { guide: Guide.SyncGuide }>
): Flows.FailureReason[] {
    const reasons: Flows.FailureReason[] = []

    if (!hasInputs(element)) return reasons

    if (currentAnswerIsEmpty(answer)) {
        if (elementRequiresAnswer(element, guideElementsContextMap)) reasons.push('required')
        return reasons
    }

    switch (element.type) {
        case 'yes-no':
        case 'confirmation':
        case 'radio':
        case 'section':
        case 'signature':
            return reasons
        case 'guide':
            // Guide elements which are required must also be marked as complete to count as done.
            if (elementRequiresAnswer(element, guideElementsContextMap) && answer.type === 'guide') {
                if (Array.isArray(answer.value)) {
                    if (answer.value.some((val) => !val.completed)) {
                        reasons.push('required')
                    }
                } else if (!answer.value.completed) {
                    reasons.push('required')
                }
            }
            return reasons

        case 'number':
            if (element.min != null && element.min > +answer.value) reasons.push('min')
            if (element.max != null && element.max < +answer.value) reasons.push('max')
            return reasons

        case 'flow':
            if (answer.type !== 'reports') return reasons // Make sure typescript knows they're the same type

            const reportCount = answer.value.length

            if (element.minReports != null && element.minReports > reportCount) reasons.push('minReports')
            if (element.maxReports != null && element.maxReports < reportCount) reasons.push('maxReports')
            if (answer.value.some(isSubtaskOpen)) reasons.push('incompleteSubtask')

            return reasons

        case 'checkbox':
        case 'image-select':
        case 'dropdown':
            if (answer.type !== element.type) return reasons // Make sure typescript knows they're the same type
            const choicesCount = answer.value.length

            if (element.minChoices != null && element.minChoices > choicesCount) reasons.push('minChoices')
            if (element.maxChoices != null && element.maxChoices < choicesCount) reasons.push('maxChoices')
            return reasons

        case 'photo':
            if (answer.type !== element.type) return reasons // Make sure typescript knows they're the same type

            if (element.minPhotos != null && element.minPhotos > answer.value.length) reasons.push('minPhotos')
            if (element.maxPhotos != null && element.maxPhotos < answer.value.length) reasons.push('maxPhotos')
            return reasons

        case 'text-capture':
            if (answer.type !== element.type) return reasons // Make sure typescript knows they're the same type

            if (element.minChars != null && element.minChars > answer.value.length) reasons.push('minChars')
            if (element.maxChars != null && element.maxChars < answer.value.length) reasons.push('maxChars')
            return reasons

        case 'datetime':
            if (answer.type !== element.type) return reasons // Make sure TypeScript knows they are the same
            if (element.selectType === 'time') return reasons // No validation makes sense currently on times
            if (answer.value.type === 'time') return reasons

            const date = answerValueToDate(answer.value, reportStartDate)
            const difference = (date.getTime() - reportStartDate.getTime()) / oneDayInMs

            const differenceAtMidnight =
                (new Date(date).setHours(0, 0, 0, 0) - new Date(reportStartDate).setHours(0, 0, 0, 0)) / oneDayInMs

            const differenceInDays = answer.value.timezoneOffsetInMs != null ? difference : differenceAtMidnight

            if (element.futureLimitInDays != null) {
                if (differenceInDays > element.futureLimitInDays) reasons.push('futureLimitInDays')
            }

            if (element.pastLimitInDays != null) {
                if (differenceInDays < -element.pastLimitInDays) reasons.push('pastLimitInDays')
            }

            return reasons

        case 'unit-select':
            if (answer.type !== element.type) return reasons // Make sure typescript knows they're the same type
            if (answer.value === 'no-units-available') return reasons

            const unitsCount = answer.value.length
            if (element.minUnits != null && element.minUnits > unitsCount) reasons.push('minUnits')
            if (element.maxUnits != null && element.maxUnits < unitsCount) reasons.push('maxUnits')
            return reasons

        case 'files-select':
            if (answer.type !== element.type) return reasons // Make sure typescript knows they're the same type

            const fileCount = answer.value.length
            if (element.uploadLimit != null && element.uploadLimit < fileCount) reasons.push('uploadLimit')

            return reasons

        default:
            assumeExhaustion(element)
            return reasons
    }
}
